import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZodFormattedError } from 'zod';
import { ButtonAttributes } from '@celebration/design-system/dist/lib/button-group/props.types';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ItemsFooterType } from '@celebration/react/ClbModal/ClbModal.types';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import ICarrierFormFullPageFlowProps from './interfaces/ICarrierFormFullPageFlowProps';
import { IContactErrors } from '../../../branches/interfaces/IBranchFormDataErrors';
import CarrierFormSteps from './enums/steps.enum';
import ICarrierFormData from '../../../../common/interfaces/ICarrierFormData';
import {
    ContactType,
    ICarriersContact,
} from '../../../../common/interfaces/IContacts';
import FullPageFlow from '../../../../common/components/full-page-flow';
import Overview from '../../../../common/components/carriers/overview';
import IHandleCarrierFormChangeProps, {
    EventType,
} from './interfaces/IHandleCarrierFormChangeProps';
import CarrierFormInfos from './components/carrier-form-infos/carrier-form-infos';
import CarrierFormService from './services/carrier-form.service';
import ICarrierFormDataErrors from '../../interfaces/ICarrierFormDataErrors';
import BranchesConstants from '../../../branches/constants';
import ICarrierInfo from '../../../branches/interfaces/ICarrierInfo';
import ReactivateModal from './components/reactivate-modal';
import ModalExitButtons from '../../../../common/components/modal';

function CarrierFormFullPageFlow({
    isCarrierFormOpened,
    setIsCarrierFormOpened,
    setCarrierId,
    carrierId,
}: Readonly<ICarrierFormFullPageFlowProps>) {
    const { setToast } = useToast();
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>();
    const generalInfoValidatedOnce = useRef(false);
    const navigate = useNavigate();
    const [isOpenReactivateModal, setIsOpenReactivateModal] = useState(false);

    const emptyErrors = useMemo(() => {
        return {} as ZodFormattedError<IContactErrors>;
    }, []);

    const [errors, setErrors] = useState(emptyErrors);

    const [isExitModalOpen, setIsExitModalOpen] = useState(false);

    const fetchCarrierData = useCallback(async () => {
        const data = await CarrierFormService.getCarrierById(carrierId!, t);

        const isLegacyCarrier = CarrierFormService.isLegacyCarrier(
            data.sulaHubId,
        );

        setCarrierData({
            id: isLegacyCarrier ? data.sulaHubId.substring(1) : undefined,
            tradeName: data.tradeName,
            paymentTerm: Intl.NumberFormat('pt-BR').format(data.paymentTerm),
            carriersContact: data.contacts.length
                ? data.contacts[0]
                : ({
                      contactType: ContactType.operational,
                  } as ICarriersContact),
            legacy: isLegacyCarrier,
        });
    }, [carrierId, t]);

    useEffect(() => {
        if (carrierId !== undefined) {
            fetchCarrierData();
        }
    }, [carrierId, fetchCarrierData]);

    const emptyCarrierData = useMemo(() => {
        return {
            legacy: false,
            carriersContact: {
                contactType: ContactType.operational,
            } as ICarriersContact,
        } as ICarrierFormData;
    }, []);

    const [activeStep, setActiveStep] = useState<CarrierFormSteps>(
        CarrierFormSteps.info,
    );
    const [carrierData, setCarrierData] =
        useState<ICarrierFormData>(emptyCarrierData);
    const [
        isShowingCarrierFormConfirmationModal,
        setIsShowingCarrierFormConfirmationModal,
    ] = useState(false);

    window.onbeforeunload = (e: any) => {
        if (isCarrierFormOpened) {
            e.preventDefault();
            return e;
        }
    };

    const fullPageFlowTitle = useMemo(() => {
        const step_title = !carrierId
            ? 'NovaTransportadora'
            : 'EditarTransportadora';

        switch (activeStep) {
            case CarrierFormSteps.info:
                return `${t(step_title)} ${t('TransportadoraEtapa1')}`;
            case CarrierFormSteps.review:
                return `${t(step_title)} ${t('TransportadoraEtapa2')}`;
        }
    }, [activeStep, carrierId, t]);

    const closeCarrierFormHandler = useCallback(() => {
        setIsExitModalOpen(true);
    }, []);

    const closeExitModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
    }, []);

    const closeReactivateModalHandler = useCallback(() => {
        setIsOpenReactivateModal(false);
    }, []);

    const showCarrierFormConfirmationModal = useCallback(() => {
        setIsShowingCarrierFormConfirmationModal(true);
    }, []);

    const closeCarrierFormConfirmationModal = useCallback(() => {
        setIsShowingCarrierFormConfirmationModal(false);
    }, []);

    const goToPreviousStep = useCallback(() => {
        setActiveStep(activeStep - 1);
    }, [activeStep]);

    const validEgatesId = useCallback(
        async (carrierData: ICarrierFormData) => {
            let response = true;

            if (carrierData?.id || carrierData?.legacy) {
                const validIdEgatesResult =
                    await CarrierFormService.validEgatesId(
                        carrierData,
                        t,
                        undefined,
                        carrierId,
                    );

                if (!validIdEgatesResult || !validIdEgatesResult.success) {
                    setErrors(
                        validIdEgatesResult.errors as ZodFormattedError<ICarrierFormDataErrors>,
                    );
                    response = false;
                }
            }

            return response;
        },
        [carrierId, t],
    );

    const validateInfos = useCallback((carrierData: ICarrierFormData) => {
        const infoResult = CarrierFormService.validateInfos(carrierData);

        if (!infoResult || !infoResult.success) {
            setErrors(
                infoResult.errors as ZodFormattedError<ICarrierFormDataErrors>,
            );
            return false;
        }

        return true;
    }, []);

    const revalidateGeneralInfo = useCallback(
        (carrierData: ICarrierFormData) => {
            if (generalInfoValidatedOnce.current) {
                if (!validateInfos(carrierData)) return;

                setErrors(emptyErrors);
            }
        },
        [validateInfos, emptyErrors],
    );

    const validateCarrierFormInfoStep = useCallback(
        async (carrierData: ICarrierFormData) => {
            generalInfoValidatedOnce.current = true;

            if (!validateInfos(carrierData)) {
                return false;
            }

            if (!(await validEgatesId(carrierData))) {
                return false;
            }

            setErrors(emptyErrors);

            return true;
        },
        [emptyErrors, validEgatesId, validateInfos],
    );

    const goToReviewStep = useCallback(async () => {
        setIsLoading(true);

        const validInfo = await validateCarrierFormInfoStep(carrierData);

        const egatesIdExistsAsDeleted = await CarrierFormService.validEgatesId(
            carrierData,
            t,
            true,
        );

        setIsLoading(false);

        if (validInfo) {
            egatesIdExistsAsDeleted.success
                ? setActiveStep(CarrierFormSteps.review)
                : setIsOpenReactivateModal(true);
        }
    }, [validateCarrierFormInfoStep, carrierData, t]);

    const reactivateAccepted = useCallback(() => {
        setIsOpenReactivateModal(false);
        setActiveStep(CarrierFormSteps.review);
    }, []);

    const goToNextStep = useCallback(() => {
        goToReviewStep();
    }, [goToReviewStep]);

    const getErrorMessage = useCallback(
        (e: any) => {
            let message = t('TransportadoraErroAdicao');

            if (e instanceof Error) {
                message = e.message;
            }
            return message;
        },
        [t],
    );

    const closeModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
        setIsCarrierFormOpened(false);
        setActiveStep(CarrierFormSteps.info);
        setCarrierData(emptyCarrierData);
        setErrors(emptyErrors);
        setCarrierId(undefined);
        generalInfoValidatedOnce.current = false;
    }, [emptyErrors, emptyCarrierData, setIsCarrierFormOpened, setCarrierId]);

    const headerFullPageFlowButtonList: ButtonAttributes[] = useMemo(() => {
        const headerButtons: ButtonAttributes[] = [];

        if (activeStep === CarrierFormSteps.info) {
            headerButtons.push({
                id: 'close-button',
                label: t('Fechar'),
                disabled: false,
                loading: false,
                title: t('TransportadoraFecharCadastro'),
                ariaLabel: t('TransportadoraFecharCadastro'),
                dataTestid: 'close-button',
                callback: closeCarrierFormHandler,
            });
        }

        if (activeStep === CarrierFormSteps.review) {
            headerButtons.push({
                id: 'back-button',
                label: t('Voltar'),
                disabled: false,
                loading: false,
                title: t('TransportadoraVoltarCadastroAnterior'),
                ariaLabel: t('TransportadoraVoltarCadastroAnterior'),
                dataTestid: 'back-button',
                callback: goToPreviousStep,
            });
        }

        if (activeStep === CarrierFormSteps.info) {
            headerButtons.push({
                id: 'next-button',
                label: t('Avançar'),
                disabled: false,
                loading: false,
                title: t('TransportadoraAvancarCadastro'),
                ariaLabel: t('TransportadoraAvancarCadastro'),
                dataTestid: 'next-button',
                callback: goToNextStep,
            });
        }

        if (activeStep === CarrierFormSteps.review) {
            headerButtons.push({
                id: 'finish-button',
                label: t('Finalizar'),
                disabled: false,
                loading: false,
                title: t('TransportadoraFinalizarCadastro'),
                ariaLabel: t('TransportadoraFinalizarCadastro'),
                dataTestid: 'finish-button',
                callback: showCarrierFormConfirmationModal,
            });
        }

        return headerButtons;
    }, [
        activeStep,
        closeCarrierFormHandler,
        goToPreviousStep,
        goToNextStep,
        showCarrierFormConfirmationModal,
        t,
    ]);

    const buttonsModal: ItemsFooterType = [
        ModalExitButtons.exitButton(
            t,
            'TransportadoraSairCadastro',
            closeModalHandler,
        ),
        ModalExitButtons.backButton(
            t,
            'TransportadoraVoltarCadastro',
            setIsExitModalOpen,
        ),
    ];

    const renderExitConfirmationCarrierFormModal = () => {
        if (isExitModalOpen) {
            return (
                <ClbModal
                    title={
                        carrierId
                            ? t('TransportadoraCancelarAtualizacao')
                            : t('TransportadoraCancelarAdicao')
                    }
                    open={isExitModalOpen}
                    onClose={closeExitModalHandler}
                    description={t('TransportadoraConfirmarSaida')}
                    type="danger"
                    itemsFooter={buttonsModal}
                />
            );
        }
    };

    const submitCarrierData = useCallback(async () => {
        setIsShowingCarrierFormConfirmationModal(false);
        setIsLoading(true);
        let toastError = true;
        let carrierId: string;
        try {
            carrierId = await CarrierFormService.saveCarrier(carrierData, t);

            toastError = false;

            setToast('positive', t('Sucesso'), t('TransportadoraCadastrada'));
        } catch (e: any) {
            if (toastError) {
                setToast('negative', t('Erro'), getErrorMessage(e));
            }
        } finally {
            closeModalHandler();
            setIsLoading(false);
            setTimeout(function () {
                navigate(BranchesConstants.PATH, {
                    state: {
                        carrierId,
                        carrierName: carrierData.tradeName,
                    } as ICarrierInfo,
                });
            }, 500);
        }
    }, [
        carrierData,
        closeModalHandler,
        getErrorMessage,
        navigate,
        setToast,
        t,
    ]);

    const updateCarrierData = useCallback(async () => {
        setIsShowingCarrierFormConfirmationModal(false);
        setIsLoading(true);
        let toastError = true;

        try {
            await CarrierFormService.updateCarrier(carrierId!, carrierData, t);

            toastError = false;

            setToast('positive', t('Sucesso'), t('TransportadoraAtualizada'));
        } catch (e: any) {
            if (toastError) {
                setToast('negative', t('Erro'), getErrorMessage(e));
            }
        } finally {
            closeModalHandler();
            setIsLoading(false);
        }
    }, [
        carrierData,
        carrierId,
        closeModalHandler,
        getErrorMessage,
        setToast,
        t,
    ]);

    const renderCarrierFormConfirmationModal = () => {
        if (isShowingCarrierFormConfirmationModal) {
            return (
                <ClbModal
                    title={
                        carrierId
                            ? t('TransportadorConfirmaEdicao')
                            : t('TransportadorConfirmaCriacao')
                    }
                    open
                    onClose={closeCarrierFormConfirmationModal}
                    description={
                        carrierId
                            ? t('TransportadorConfirmaFinalizacaoEdicao')
                            : t('TransportadorConfirmaFinalizacaoCriacao')
                    }
                    itemsFooter={[
                        {
                            key: 'new-carrier-confirmation-model-yes-button',
                            id: 'new-carrier-confirmation-model-yes-button',
                            label: t('Sim'),
                            disabled: false,
                            loading: false,
                            title: t('TransportadorConfirmarCriacao'),
                            'data-testid':
                                'new-carrier-confirmation-modal-yes-button',
                            onClick: async () => {
                                carrierId
                                    ? await updateCarrierData()
                                    : await submitCarrierData();
                            },
                            role: 'button',
                            size: 'md',
                        },
                        {
                            key: 'new-carrier-confirmation-model-no-button',
                            id: 'new-carrier-confirmation-model-no-button',
                            label: t('Não'),
                            disabled: false,
                            loading: false,
                            title: t('TransportadoraRetornar'),
                            'data-testid':
                                'new-carrier-confirmation-modal-no-button',
                            onClick: _ => closeCarrierFormConfirmationModal(),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                        },
                    ]}
                />
            );
        }
    };

    const handleCarrierFormChange = useCallback(
        (handleCarrierFormChangeProps: IHandleCarrierFormChangeProps) => {
            let updatedCarrierData = { ...carrierData };

            if (!handleCarrierFormChangeProps.eventType) {
                updatedCarrierData = {
                    ...carrierData,
                    [handleCarrierFormChangeProps.property]:
                        handleCarrierFormChangeProps.newValue,
                };
                if (
                    handleCarrierFormChangeProps.property === 'legacy' &&
                    handleCarrierFormChangeProps.newValue === false
                ) {
                    updatedCarrierData.id = '';
                }
            }

            if (
                handleCarrierFormChangeProps.eventType ===
                EventType.ContactChange
            ) {
                updatedCarrierData.carriersContact = {
                    ...carrierData.carriersContact,
                    [handleCarrierFormChangeProps.property]:
                        handleCarrierFormChangeProps.newValue,
                };
            }

            revalidateGeneralInfo(updatedCarrierData);
            setCarrierData(updatedCarrierData);
        },
        [carrierData, revalidateGeneralInfo],
    );

    return (
        <>
            <FullPageFlow
                title={fullPageFlowTitle}
                type="progress"
                opened={isCarrierFormOpened}
                onClbClose={closeCarrierFormHandler}
                steps={1}
                buttonsList={headerFullPageFlowButtonList}
                activeStep={activeStep}
                data-testid="full-page-flow"
            >
                {isLoading && <ClbLoading />}

                {isCarrierFormOpened &&
                    activeStep === CarrierFormSteps.info &&
                    !isLoading && (
                        <CarrierFormInfos
                            handleCarrierFormChange={handleCarrierFormChange}
                            carrierFormData={carrierData}
                            inputErrors={errors}
                            isEditForm={!!carrierId}
                        />
                    )}

                {isCarrierFormOpened &&
                    activeStep === CarrierFormSteps.review &&
                    !isLoading && (
                        <Overview
                            carrierData={carrierData}
                            carrierContactData={carrierData.carriersContact}
                        />
                    )}
            </FullPageFlow>

            {renderExitConfirmationCarrierFormModal()}
            {renderCarrierFormConfirmationModal()}

            {isOpenReactivateModal && (
                <ReactivateModal
                    opened={isOpenReactivateModal}
                    closeModalCallback={closeReactivateModalHandler}
                    confirmModalCallback={reactivateAccepted}
                />
            )}
        </>
    );
}

export default CarrierFormFullPageFlow;
